import { useMemo } from 'react';

import { mapDocumentToWriteDocument } from '#api/adapters/documentAdapter.ts';
import {
    useGetOrderDocumentsQuery,
    useGetOrderDocumentsQuery as useGetOrderDocumentsV2Query,
    useGetStopDocumentsQuery as useGetStopDocumentsV2Query,
    useGetTourDocumentsQuery,
    useUploadStopDocumentMutation as useUploadStopDocumentV2Mutation,
    useUploadTourDocumentMutation as useUploadTourDocumentV2Mutation,
} from '#api/documentApi.slice.ts';
import type { RTKQueryOptions } from '#api-bridge/utils/rktQuery.utils.ts';
import type { DocumentList } from '#domain/Document.ts';

import { dedupeDocumentsById } from './utils/dedupeDocumentsById.ts';
import { toOrderDetailsDocuments } from './utils/toOrderDetailsDocuments.ts';

export type UploadDocumentTarget = { stopId: string; tourId?: never } | { stopId?: never; tourId: string };
export type UploadDocumentPayload = {
    list: DocumentList;
} & UploadDocumentTarget;

export const useGetOrderDetailsDocumentsQuery = (
    arg: {
        orderId: string;
        loadingStopId?: string;
        unloadingStopId?: string;
        executionStopId?: string;
    },
    options?: RTKQueryOptions
) => {
    const orderDocumentsQuery = useGetOrderDocumentsV2Query({ orderId: arg.orderId }, options);
    const loadingStopDocumentsQuery = useGetStopDocumentsV2Query(
        { stopId: arg.loadingStopId ?? '' },
        { ...options, skip: options?.skip === true || !arg.loadingStopId }
    );
    const unloadingStopDocumentsQuery = useGetStopDocumentsV2Query(
        { stopId: arg.unloadingStopId ?? '' },
        { ...options, skip: options?.skip === true || !arg.unloadingStopId }
    );
    const executionStopDocumentsQuery = useGetStopDocumentsV2Query(
        { stopId: arg.executionStopId ?? '' },
        { ...options, skip: options?.skip === true || !arg.executionStopId }
    );

    const data = useMemo(() => {
        if (options?.skip === true) {
            return undefined;
        }
        const documentSources = [
            orderDocumentsQuery.data?.items ?? [],
            loadingStopDocumentsQuery.data?.items ?? [],
            unloadingStopDocumentsQuery.data?.items ?? [],
            executionStopDocumentsQuery.data?.items ?? [],
        ];
        const totalDocumentsCount = documentSources.reduce((sum, documents) => sum + documents.length, 0);
        if (totalDocumentsCount === 0) {
            return undefined;
        }

        const nonEmptySources = documentSources.filter(documents => documents.length > 0);
        const mergedDocuments =
            nonEmptySources.length <= 1
                ? (nonEmptySources[0] ?? [])
                : dedupeDocumentsById([
                      ...documentSources[0],
                      ...documentSources[1],
                      ...documentSources[2],
                      ...documentSources[3],
                  ]);

        return toOrderDetailsDocuments(mergedDocuments, arg.loadingStopId, arg.unloadingStopId, arg.executionStopId);
    }, [
        arg,
        options,
        executionStopDocumentsQuery.data?.items,
        loadingStopDocumentsQuery.data?.items,
        orderDocumentsQuery.data?.items,
        unloadingStopDocumentsQuery.data?.items,
    ]);

    return {
        data,
    };
};

export const useGetStopDocumentsQuery = (arg: { stopId: string }, options?: RTKQueryOptions) => {
    return useGetStopDocumentsV2Query(arg, options);
};

export const useUploadDocumentMutation = () => {
    const [uploadTourV2] = useUploadTourDocumentV2Mutation();
    const [uploadStop] = useUploadStopDocumentV2Mutation();

    const uploadDocument = ({ list, stopId, tourId }: UploadDocumentPayload) => {
        const document = list.items[0];
        if (!document) {
            throw new Error('No document provided for upload');
        }

        const writeDocument = mapDocumentToWriteDocument(document);
        if (stopId) {
            return uploadStop({ stopId, document: writeDocument });
        }

        if (tourId) {
            return uploadTourV2({ tourId, document: writeDocument });
        }

        throw new Error('No stopId or tourId provided for document upload');
    };

    return [uploadDocument] as const;
};

export { useGetOrderDocumentsQuery, useGetTourDocumentsQuery };
